import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../../components/ToastMsg";
import "react-loading-skeleton/dist/skeleton.css";
import styles from './index.module.css';
import { useParams } from "react-router-dom";
import { getVehicleById } from "../../../../redux/features/vehicle/vehicleService";
import GridLoader from "react-spinners/GridLoader";
import BeatLoader from "react-spinners/BeatLoader";
import { addNewVehicleCost } from "../../../../redux/features/vehicleCost/vehicleCostService";
import { NumericFormat } from "react-number-format";

const VehicleCostSheet = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const { vehicle, isVehicleLoading } = useSelector((state) => state.vehicle.vehicle);
    const [disabledBtn, setBtnDisable] = useState(false);
    const [formData, setFormData] = useState({});
    const [fobCost, setFobCost] = useState(0.0);
    const [cnf, setCnf] = useState(0.0);
    const [jAndACharge, setJandACharge] = useState(0.0);
    const [isSellingErrorMsg, setIsSellingErrorMsg] = useState(false);
    const [sellingError, setSellingError] = useState('');
    const { isVehicleCostCreating } = useSelector((state) => state.vehicleCost.newVehicleCost);

    const getVehicleData = async () => {
        try {
            const response = await dispatch(getVehicleById({ id })).unwrap();

            const vehicle = response.vehicle_costs[0];

            const form = {
                buying_price: vehicle.buying_price ?? 0,
                auction_fee: vehicle.auction_fee ?? 0,
                local_transport: vehicle.local_transport ?? 0,
                ec_cost: vehicle.ec_cost ?? 0,
                repairing_cost: vehicle.repairing_cost ?? 0,
                freight: vehicle.freight ?? 0,
                selling_price: vehicle.selling_price ?? 0,
            };

            // Calculate derived costs
            const fobFields = ['buying_price', 'auction_fee', 'local_transport', 'ec_cost', 'repairing_cost'];
            const fob = fobFields.reduce((sum, field) => sum + (parseFloat(form[field]) || 0), 0);

            const cnf = fob + (parseFloat(form.freight) || 0);
            const jAndA = (parseFloat(form.selling_price) || 0) - cnf;

            // Set states
            setFormData(form);
            setFobCost(Number(fob.toFixed(2)));
            setCnf(Number(cnf.toFixed(2)));
            setJandACharge(Number(jAndA.toFixed(2)));
        } catch (error) {
        }
    };
    useEffect(() => {
        getVehicleData();
    }, [dispatch, id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let numericValue = parseFloat(value);

        // Block negative values
        if (numericValue < 0 || isNaN(numericValue)) {
            numericValue = 0;
        }

        setFormData((prev) => {
            const updatedFormData = {
                ...prev,
                [name]: numericValue,
            };

            // Recalculate FOB
            const fobFields = ['buying_price', 'auction_fee', 'local_transport', 'ec_cost', 'repairing_cost'];
            const fobTotal = fobFields.reduce((sum, field) => sum + (parseFloat(updatedFormData[field]) || 0), 0);
            setFobCost(fobTotal.toFixed(2));

            // Recalculate CNF
            const cnfFields = [...fobFields, 'freight'];
            const cnfTotal = cnfFields.reduce((sum, field) => sum + (parseFloat(updatedFormData[field]) || 0), 0);
            setCnf(cnfTotal.toFixed(2));

            // Recalculate J&A Charge
            const sellingPrice = parseFloat(updatedFormData['selling_price']) || 0;
            const jAndACharge = sellingPrice - cnfTotal;
            setJandACharge(jAndACharge.toFixed(2));

            return updatedFormData;
        });

    };

    const insertCostSheet = async () => {
        try {
            if (formData.selling_price === 0 || formData.selling_price === undefined) {
                setIsSellingErrorMsg(true);
                setSellingError('Please insert selling price.');
            } if (formData.selling_price < +cnf) {
                setIsSellingErrorMsg(true);
                setSellingError('Selling price must be greater than the CNF.');
            } else {
                formData.vehicle_id = +id;
                formData.fob_cost = +fobCost;
                formData.cnf_cost = +cnf;
                formData.j_and_a_charge = +jAndACharge;
                const response = await dispatch(addNewVehicleCost(formData)).unwrap();
                setBtnDisable(false);
                getVehicleData();
                showToast('success', response.message);
            }
        }
        catch (error) {
            setBtnDisable(false);
            showToast('error', error.message);
        };
    }

    return (
        <div className={`card-section`}>
            {isVehicleLoading ? (
                <div className={`loader-style`}>
                    <GridLoader color="#c93b25" />
                </div>
            ) : (
                <>
                    <div className={styles.vehicleCard}>
                        <img src={vehicle?.image_url} alt="Thumbnail" className={styles.thumbImg} />
                        <div className={styles.vehicleDetails}>
                            <p className={styles.vehicleName}>{vehicle?.name}</p>
                            <div className={styles.colSection}>
                                <div className={styles.detailsRows}>
                                    <div className={styles.subSec}>
                                        <p className={styles.title}>Stock (Ref#)</p>
                                        <p className={styles.titleValue}>{vehicle?.stock_no}</p>
                                    </div>
                                    <div className={styles.subSec}>
                                        <p className={styles.title}>Chassis No</p>
                                        <p className={styles.titleValue}>{vehicle?.chassis_no}</p>
                                    </div>
                                </div>
                                <div className={styles.detailsRows}>
                                    <div className={styles.subSec}>
                                        <p className={styles.title}>Mileage</p>
                                        <p className={styles.titleValue}>{vehicle?.mileage} km</p>
                                    </div>
                                    <div className={styles.subSec}>
                                        <p className={styles.title}>Color</p>
                                        <p className={styles.titleValue}>{vehicle?.exterior_color.name}</p>
                                    </div>
                                </div>
                                <div className={styles.detailsRows}>
                                    <div className={styles.subSec}>
                                        <p className={styles.title}>Engine</p>
                                        <p className={styles.titleValue}>{vehicle?.engine_capacity} cc</p>
                                    </div>
                                    <div className={styles.subSec}>
                                        <p className={styles.title}>Steering</p>
                                        <p className={styles.titleValue}>{vehicle?.steering}</p>
                                    </div>
                                </div>
                                <div className={styles.detailsRows}>
                                    <div className={styles.subSec}>
                                        <p className={styles.title}>Fuel</p>
                                        <p className={styles.titleValue}>{vehicle?.fuel.name}</p>
                                    </div>
                                    <div className={styles.subSec}>
                                        <p className={styles.title}>Transmission</p>
                                        <p className={styles.titleValue}>{vehicle?.transmission.name}</p>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.featureSec}>
                                <p className={styles.title}>Features </p>
                                {vehicle?.vehicle_features.map((feature, index) => (
                                    <span key={feature?.feature.id} className={styles.titleValue}>
                                        {feature?.feature.name}
                                        {vehicle.vehicle_features.length > index + 1 ? ` | ` : ""}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
            <div className={`main-card`}>
                <div className={styles.headerRow}>
                    <h1 className={`form-title`}>Cost Sheet</h1>
                    <button
                        className={`btn-primary`}
                        type='button'
                        disabled={disabledBtn}
                        onClick={insertCostSheet}
                    >
                        {isVehicleCostCreating ? <BeatLoader color='#ffffff' /> : `Submit the Cost Sheet`}
                    </button>
                </div>
                <div className={styles.formContainer}>
                    <form className={styles.coordinatorForm} noValidate>
                        <table className={styles.table}>
                            <tbody className={styles.tbody}>
                                {/* {[
                                    "buying_price",
                                    "auction_fee",
                                    "local_transport",
                                    "ec_cost",
                                    "repairing_cost",
                                    "fob_cost",
                                    "freight",
                                    "cnf_cost",
                                    "j_and_a_charge",
                                    "other_cost"
                                ].map((field) => (
                                    <tr key={field}>
                                        <td className={styles.tableCell}>
                                            {field.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
                                        </td>
                                        <td className={styles.tableCellValue}>
                                            <input
                                                type="number"
                                                id={field}
                                                name={field}
                                                value={parseFloat(formData[field]) || ""}
                                                onChange={handleInputChange}
                                                placeholder={`Enter ${field.includes("ec") ? "EC Cost" :
                                                    field.includes("fob") ? "FOB Cost" :
                                                        field.includes("cnf") ? "CNF Cost" :
                                                            field.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
                                                    }`}
                                                className={styles.formInputCost}
                                                step="0.01" // Allow floating values
                                            />
                                        </td>
                                    </tr>
                                ))} */}
                                <tr>
                                    <td className={styles.tableCell}>Buying Price</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="buying_price"
                                            name="buying_price"
                                            value={formData['buying_price']}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Enter buying price"
                                            className={styles.formInputCost}
                                            onValueChange={(values) => {
                                                const { value } = values; // unformatted value
                                                handleInputChange({
                                                    target: {
                                                        name: "buying_price",
                                                        value: value,
                                                    },
                                                });
                                            }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles.tableCell}>Auction Fee</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="auction_fee"
                                            name="auction_fee"
                                            value={formData['auction_fee']}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Enter auction fee"
                                            className={styles.formInputCost}
                                            onValueChange={(values) => {
                                                const { value } = values; // unformatted value
                                                handleInputChange({
                                                    target: {
                                                        name: "auction_fee",
                                                        value: value,
                                                    },
                                                });
                                            }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles.tableCell}>Local Transport Fee</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="local_transport"
                                            name="local_transport"
                                            value={formData['local_transport']}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Enter local transport fee"
                                            className={styles.formInputCost}
                                            onValueChange={(values) => {
                                                const { value } = values; // unformatted value
                                                handleInputChange({
                                                    target: {
                                                        name: "local_transport",
                                                        value: value,
                                                    },
                                                });
                                            }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles.tableCell}>Ec Cost</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="ec_cost"
                                            name="ec_cost"
                                            value={formData['ec_cost']}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Enter ec cost"
                                            className={styles.formInputCost}
                                            onValueChange={(values) => {
                                                const { value } = values; // unformatted value
                                                handleInputChange({
                                                    target: {
                                                        name: "ec_cost",
                                                        value: value,
                                                    },
                                                });
                                            }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles.tableCell}>Repairing Cost</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="repairing_cost"
                                            name="repairing_cost"
                                            value={formData['repairing_cost']}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Enter repairing cost"
                                            className={styles.formInputCost}
                                            onValueChange={(values) => {
                                                const { value } = values; // unformatted value
                                                handleInputChange({
                                                    target: {
                                                        name: "repairing_cost",
                                                        value: value,
                                                    },
                                                });
                                            }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles.tableCell}>FOB</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="fobCost"
                                            name="fobCost"
                                            value={fobCost}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Fob"
                                            className={styles.formInputCost}
                                            disabled
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles.tableCell}>Freight</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="freight"
                                            name="freight"
                                            value={formData['freight']}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Enter freight"
                                            className={styles.formInputCost}
                                            onValueChange={(values) => {
                                                const { value } = values; // unformatted value
                                                handleInputChange({
                                                    target: {
                                                        name: "freight",
                                                        value: value,
                                                    },
                                                });
                                            }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles.tableCell}>CNF</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="cnf"
                                            name="cnf"
                                            value={cnf}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Cnf"
                                            className={styles.formInputCost}
                                            disabled
                                        />
                                    </td>
                                </tr>
                                {/* <tr>
                                    <td className={styles.tableCell}>Other Costs</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="other_cost"
                                            name="other_cost"
                                            value={formData['other_cost']}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Enter other cost"
                                            className={styles.formInputCost}
                                            onValueChange={(values) => {
                                                const { value } = values; // unformatted value
                                                handleInputChange({
                                                    target: {
                                                        name: "other_cost",
                                                        value: value,
                                                    },
                                                });
                                            }}
                                        />
                                    </td>
                                </tr> */}
                                <tr>
                                    <td className={styles.tableCell}>Company Charge</td>
                                    <td className={styles.tableCellValue}>
                                        <NumericFormat
                                            id="j_and_a_charge"
                                            name="j_and_a_charge"
                                            value={jAndACharge}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Enter CarXchanger cost"
                                            className={styles.formInputCost}
                                            disabled
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles.tableCell}>Selling Price</td>
                                    <td className={styles.tableCellValue}>
                                        {/* <input
                                            type="number"
                                            id="selling_price"
                                            name="selling_price"
                                            value={parseFloat(formData['selling_price']) || ""}
                                            onChange={handleInputChange}
                                            placeholder="Enter selling price"
                                            className={styles.formInputSell}
                                            step="0.01"
                                        /> */}
                                        <NumericFormat
                                            id="selling_price"
                                            name="selling_price"
                                            value={formData['selling_price']}
                                            thousandSeparator=","
                                            decimalSeparator="."
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowNegative={false}
                                            placeholder="Enter selling price"
                                            className={styles.formInputSell}
                                            onValueChange={(values) => {
                                                const { value } = values; // unformatted value
                                                handleInputChange({
                                                    target: {
                                                        name: "selling_price",
                                                        value: value,
                                                    },
                                                });
                                                setIsSellingErrorMsg(false);
                                            }}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        {isSellingErrorMsg && <p className={`text-danger`}>{sellingError}</p>}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default VehicleCostSheet;